/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: pages\dashboards\widgets\HardwareInfoWidget.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the PBX Tools module.
----------------------------------------------------------------*/


import { useState, useEffect, useContext } from 'react';
import { Cpu, HardDrive, Database, CircuitBoard } from 'lucide-react';
import { WidgetContext } from '../components/WidgetWrapper';

interface DiskUsage {
    filesystem: string;
    size: string;
    used: string;
    available: string;
    usePercent: number;
    mountpoint: string;
}

interface HardwareInfo {
    cpu?: {
        model: string;
        cores: number;
        usage: number;
    };
    memory?: {
        total: number;
        free: number;
        used: number;
        usage: number;
    };
    disk?: DiskUsage[];
    motherboard?: {
        manufacturer: string;
        product: string;
    };
}

interface HardwareInfoWidgetProps {
    config?: Record<string, any>;
    isEditMode?: boolean;
}

const formatBytes = (bytes: number, decimals = 1) => {
    if (!+bytes) return '0 B';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
};

// Helper to convert typical "50G" string to human readable if needed, 
// but disk usage usually comes as strings like "50G" from df. 
// We will just use the strings from API for Disk.

export function HardwareInfoWidget({ isEditMode }: HardwareInfoWidgetProps) {
    const [info, setInfo] = useState<HardwareInfo | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const widgetPrefs = useContext(WidgetContext);
    const borderColor = widgetPrefs?.borderColor || 'rgba(255,255,255,0.1)';

    useEffect(() => {
        const fetchInfo = async () => {
            try {
                const protocol = window.location.protocol;
                const host = window.location.host;
                const response = await fetch(`${protocol}//${host}/monitoring-api/api/system/stats`);

                if (response.ok) {
                    const data = await response.json();
                    if (data.success && data.data) {
                        setInfo({
                            cpu: data.data.cpu,
                            memory: data.data.memory,
                            disk: data.data.disk,
                            motherboard: data.data.motherboard
                        });
                        setError(null);
                    }
                }
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } catch (err: any) {
                setError('Failed to load hardware info');
            } finally {
                setLoading(false);
            }
        };

        fetchInfo();
        const interval = setInterval(fetchInfo, 30000);
        return () => clearInterval(interval);
    }, []);

    if (loading) {
        return (
            <div className="h-full flex items-center justify-center">
                <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    if (error || !info) {
        return (
            <div className="h-full flex items-center justify-center text-muted-foreground">
                <span className="text-sm">{error || 'No data available'}</span>
            </div>
        );
    }

    const rootDisk = info.disk?.find(d => d.mountpoint === '/') || info.disk?.[0];

    // Format Memory strings
    const memUsed = info.memory ? formatBytes(info.memory.used) : '0 GB';
    const memTotal = info.memory ? formatBytes(info.memory.total) : '0 GB';

    // Format Disk strings
    // API returns disk sizes as strings like "10G". We might want to parse if we wanted consistent GB,
    // but assuming `df -h` output is readable enough.
    // The reference image shows: "X GB Used / Y GB Free"
    // Our API `available` is Free.
    const diskUsed = rootDisk ? rootDisk.used : '0';
    const diskFree = rootDisk ? rootDisk.available : '0';

    const items = [
        {
            label: 'Motherboard',
            value: info.motherboard
                ? `${info.motherboard.manufacturer} ${info.motherboard.product}`
                : 'Unknown'
        },
        {
            label: 'CPU Model',
            value: info.cpu?.model.split('@')[0].trim() || 'Unknown' // Clean up Intel names
        },
        {
            label: 'CPU Cores',
            value: info.cpu?.cores || '0'
        },
        {
            label: 'RAM',
            value: `${memUsed} / ${memTotal}`
        },
        {
            label: 'Disk Usage',
            value: `${diskUsed} Used / ${diskFree} Free`
        }
    ];

    return (
        <div className="h-full pr-1 overflow-hidden flex flex-col">
            <div className="flex-1 flex flex-col justify-between pt-1 pb-1">
                {items.map((item, idx) => (
                    <div key={idx} className="flex flex-col sm:flex-row sm:justify-between sm:items-center pb-1 last:border-0 last:pb-0"
                         style={{ borderBottom: idx === items.length - 1 ? 'none' : `1px solid ${borderColor}` }}>
                        <span className="text-xs text-muted-foreground font-medium">{item.label}</span>
                        <span className="text-xs font-medium text-foreground text-right truncate max-w-[12rem]">{item.value}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}
